import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceDot,
} from "recharts";
import { CHART, METRIC_COLORS } from "../lib/designTokens";

const MARKERS = new Set([1, 5, 30, 60, 300, 1200, 3600]);

const fmtSecs = (s) => {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${Math.round((s / 3600) * 10) / 10}h`;
};

const TooltipBox = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="chart-tooltip font-mono">
      <div className="text-dark-text-secondary mb-1">{fmtSecs(p.seconds)}</div>
      <div style={{ color: METRIC_COLORS.power }}>
        {Math.round(p.watts)} <span className="text-dark-text-muted">W</span>
      </div>
    </div>
  );
};

const XTick = ({ x, y, payload }) => (
  <text
    x={x}
    y={y + 12}
    textAnchor="middle"
    fill={CHART.tick}
    fontSize={10}
    fontFamily="JetBrains Mono"
  >
    {fmtSecs(payload.value)}
  </text>
);

export const PowerCurve = ({ curve, ftp }) => {
  if (!curve || curve.length === 0) return null;

  // Log-scale x, but Recharts log scale needs strictly-positive numeric domain.
  const data = curve;
  const maxW = Math.max(...data.map((d) => d.watts));

  return (
    <div className="app-card-elevated p-5 sm:p-6" data-testid="power-curve-chart">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="card-label">Mean-Max</div>
          <h3 className="section-title mt-0.5" style={{ color: METRIC_COLORS.power }}>
            Power Curve
          </h3>
        </div>
        <div className="meta-text">Best average over each duration</div>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 20, left: -10, bottom: 20 }}>
            <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="seconds"
              type="number"
              scale="log"
              domain={[1, "dataMax"]}
              ticks={[1, 5, 30, 60, 300, 1200, 3600]}
              tick={<XTick />}
              tickLine={false}
              axisLine={{ stroke: CHART.axis }}
              height={30}
            />
            <YAxis
              stroke={CHART.tick}
              tick={{ fontSize: 10, fill: CHART.tick, fontFamily: "JetBrains Mono" }}
              tickLine={false}
              axisLine={{ stroke: CHART.axis }}
              domain={[0, Math.ceil(maxW * 1.1)]}
              width={40}
            />
            <Tooltip
              content={<TooltipBox />}
              cursor={{ stroke: METRIC_COLORS.power, strokeWidth: 1, strokeDasharray: "3 3" }}
            />
            {ftp && (
              <Line
                type="monotone"
                dataKey={() => ftp}
                stroke={CHART.tick}
                strokeDasharray="4 4"
                strokeWidth={1}
                dot={false}
                isAnimationActive={false}
                name="FTP"
              />
            )}
            <Line
              type="monotone"
              dataKey="watts"
              stroke={METRIC_COLORS.power}
              strokeWidth={2.5}
              dot={(props) => {
                const { cx, cy, payload } = props;
                if (!MARKERS.has(payload.seconds)) return null;
                return (
                  <circle
                    key={payload.seconds}
                    cx={cx}
                    cy={cy}
                    r={3.5}
                    fill={CHART.tooltipBg}
                    stroke={METRIC_COLORS.power}
                    strokeWidth={2}
                  />
                );
              }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Compact stat strip */}
      <div className="grid grid-cols-4 md:grid-cols-7 divide-x divide-[var(--border-primary)] border border-[var(--border-primary)] rounded-md overflow-hidden mt-4 bg-[var(--surface-secondary)]">
        {data
          .filter((d) => MARKERS.has(d.seconds))
          .map((d) => (
            <div
              key={d.seconds}
              className="p-3 text-center"
              data-testid={`power-curve-${d.seconds}s`}
            >
              <div className="text-[9px] tracking-[0.2em] uppercase text-dark-text-muted">
                {fmtSecs(d.seconds)}
              </div>
              <div className="font-mono text-sm mt-1" style={{ color: METRIC_COLORS.power }}>
                {Math.round(d.watts)}
                <span className="text-[9px] text-dark-text-muted ml-0.5">W</span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
